import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  OnChanges,
  ViewChild,
  ViewRef
} from '@angular/core';
import { ResolvableComponent } from '@ispm/core/decorators/resolvable-component.decorator';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { IdynamicFormItem } from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { Itab } from '@ispm/shared/interfaces/Itab';
import { cloneDeep, get, isEmpty, isNil } from 'lodash';
import { TabsHeaderComponent } from '@ispm/layout/components/tabs-header/tabs-header.component';
import { DynamicFormItem } from '@ispm/dynamic-form/classes/dynamic-form-item.class';
import { ITEMS_TYPES } from '@ispm/dynamic-form/constants/ItemsTypes';
import { FormGroup } from '@angular/forms';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { AgregationService } from '@ispm-client/modules/aggregator/services';
import { IConditionalItem } from '@ispm/shared/classes/conditional-item.class';
import { TestService } from '@ispm/dashboard/test/services/test.service';

export interface DynamicFormTabsConf {
  tabs: DynamicFormTabConf[];
  tabsHeaderCss?: string;
  forms?: string[];
  initialSelectionByDataBinding?: any;
}

interface ISelectionIdentifier {
  path: string;
  value: string;
}

export interface DynamicFormTabHeaderConf {
  title: string;
  countPath?: string;
  selected?: boolean;
  onTabSelected?: any;
  selectionIdentifier?: ISelectionIdentifier;
}

export interface DynamicFormTabConf {
  header: DynamicFormTabHeaderConf;
  displayConditions?: Array<IConditionalItem>;
  content: {
    formId: string,
    formItemFullWidth: boolean
  };
}

export enum DYNAMIC_FORM_TAB_LAYOUT {
  SECTIONS = 'sections',
  HORIZONTAL = 'horizontal'
}

@Component({
  selector: 'ispm-dynamic-tabs',
  templateUrl: './dynamic-form-tabs.component.html',
  styleUrls: ['./dynamic-form-tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@ResolvableComponent(null, 'DynamicFormTabs')
export class DynamicFormTabsComponent implements IWidgetItem, OnInit, OnChanges {
  testConfiguration: any;
  testViewVerticalLayout: boolean;

  public set widgetData(data) {
    if (!isNil(data)) {
      this._widgetData = cloneDeep(data);
      this.setCountersAtTabsHeader();

      if (!(<ViewRef> this.cd).destroyed) {
        this.cd.markForCheck();
        this.cd.detectChanges();
      }
    }
  }

  public get widgetData() {
    return this._widgetData;
  }

  set widgetConfig(widgetConfig: IdynamicFormItem<DynamicFormTabsConf>) {
    this._widgetConfig = widgetConfig;
    const widgetPayload = get(widgetConfig, 'widgetPayload', { });
    Object.assign(this, widgetPayload);
    this.preFetchForms();
    const tabsConf = widgetConfig.widgetPayload.tabs;

    for (let i = 0; i < tabsConf.length; i++) {
      this.tabsHeaders[i] = { name: tabsConf[i].header.title};
      this.tabsContents[i] = tabsConf[i].content.formId;
      this.formFullWidth[i] = isNil(tabsConf[i].content.formItemFullWidth) ? true : tabsConf[i].content.formItemFullWidth;
    }

    this.addWidgetFormControl(DynamicFormItem.builder('', '_tabActiveRecord', ITEMS_TYPES.HIDDEN).setDefault(null));
    this.tabsHeaderCss = isNil(widgetConfig.widgetPayload.tabsHeaderCss)  ? '' : widgetConfig.widgetPayload.tabsHeaderCss;
    // if (widgetConfig.additional && widgetConfig.additional.layoutDirection) {
    //   this.layoutType = widgetConfig.additional.layoutDirection
    // }
  }

  get widgetConfig(): IdynamicFormItem<DynamicFormTabsConf> {
    return this._widgetConfig;
  }

  constructor(
    private cd: ChangeDetectorRef,
    private formHandlerService: FormHandlerService,
    private aggregatorService: AgregationService,
    private testService: TestService
  ) {
  }

  public selectedTab: string;
  public tabsHeaders: Itab[] = [];
  private _widgetConfig: IdynamicFormItem<DynamicFormTabsConf>;
  public tabsContents: string[] = [];
  public _widgetData: any;
  public formFullWidth: boolean[] = [];
  public widgetHeader: string;
  public displayHeader: boolean;
  public tabsPriority: any[] = [];
  widgetUpdateValueCallback: (value) => void;


  @Input() additionalSearchValues: any;
  @Input() tabsHeaderCss: string = '';
  addWidgetFormControl: (config: IdynamicFormItem) => void;
  widgetFormInstance: FormGroup;
  @ViewChild(TabsHeaderComponent, {static: false}) tabsHeader: TabsHeaderComponent;

  forceUpdate: (value) => void;

  ngOnInit(): void {
    this.testConfiguration = this.testService.testConfiguration;
    this.testViewVerticalLayout = !!this.testConfiguration && !!this.testConfiguration.testViewVerticalLayout;
    this.setInitSelection();
  }

  ngOnChanges() {
    this.setCountersAtTabsHeader();
  }

  tabChanged(tabTitle: string) {
    if (tabTitle === this.selectedTab) {
      return;
    }
    this.selectedTab = tabTitle;
    const selectedHeaderConf = this.widgetConfig.widgetPayload.tabs.find(tab => tab.header.title === this.selectedTab);
    if (!isEmpty(selectedHeaderConf.header.onTabSelected)) {
      this.bubbleUpUpdate(selectedHeaderConf.header.onTabSelected);
    }
    this.cd.markForCheck();
  }

  private setCountersAtTabsHeader() {
    const tabsConf = this.widgetConfig.widgetPayload.tabs;

    for (let i = 0; i < tabsConf.length; i++) {
      const field = tabsConf[i].header.countPath;

      if (field) {
        if (!isEmpty(this.widgetData[field])) {
          this.tabsHeaders[i].amount = Array.isArray(this.widgetData[field])
            ? this.widgetData[field].length
            : Object['values'](this.widgetData[field]).length;
        } else {
          this.tabsHeaders[i].amount = 0;
        }
      }

      this.tabsHeaders = [...this.tabsHeaders];
    }
  }

  public tabCommunicates($event: any) {
    this.bubbleUpUpdate($event.form);
  }

  private bubbleUpUpdate(updates: any) {
    setTimeout(function () {
      if (isEmpty(this.widgetFormInstance.controls['_tabActiveRecord'])) {
        this.addWidgetFormControl(DynamicFormItem.builder('', '_tabActiveRecord', ITEMS_TYPES.HIDDEN).setDefault(null));
      }

      this.widgetFormInstance.controls['_tabActiveRecord'].patchValue(updates);
      this.widgetUpdateValueCallback({ ...this.widgetFormInstance.value[this._widgetConfig.name]});
    }.bind(this), 0);
  }

  private preFetchForms() {
    const forms = this.widgetConfig.widgetPayload.forms;

    if (forms) {
      forms.forEach(formId => this.formHandlerService.getForm(formId));
    }
  }

  private setInitSelection() {
    if (!this.selectedTab) {
      let selectedTab;
      const selectionDataBinding = this.widgetConfig.widgetPayload.initialSelectionByDataBinding;
      if (isEmpty(selectionDataBinding)) {
        let therapiesAndTrialsTabs = this.testConfiguration?.configurationsMap?.treatmentSelection?.uiOverride?.activeTabs.find((tab) => tab.type === 'therapy-prioritization');
        this.tabsPriority = therapiesAndTrialsTabs.priority;
        if (this.tabsPriority && this.tabsPriority.length > 0){
          for (let i = 0;i < this.tabsPriority.length;i ++) {
            const tabAmount = this.tabsHeaders[this.tabsPriority[i]].amount
            if (tabAmount === undefined || tabAmount > 0 || i === this.tabsPriority.length - 1){
              this.tabChanged(this.tabsHeaders[this.tabsPriority[i]].name);
              break;
            }
          }
        } else {
          selectedTab = this.getSelectionFromConfig();
          this.tabChanged(selectedTab.header.title);
        }
      } else {
        this.aggregatorService.registerQuery(selectionDataBinding).take(1).subscribe(selectionFromData => {
          selectedTab =
            this.widgetConfig.widgetPayload.tabs
              .find(tab => get(selectionFromData, tab.header.selectionIdentifier.path) === tab.header.selectionIdentifier.value);

          if (isEmpty(selectedTab)) {
            selectedTab = this.getSelectionFromConfig();
          }

          this.tabChanged(selectedTab.header.title);
        });
      }
    }
  }

  private getSelectionFromConfig() {
    const tabsConf = this._widgetConfig.widgetPayload.tabs;
    return tabsConf.find(tab => tab.header.selected);
  }

  public tabInit($event: any) {

  }

  public trackByFn(index) {
    return index;
  }
}

